import React, { createContext, useContext, useMemo, useRef } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { arrayRemove, arrayUnion, writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import { membershipDelta, sortTags, tagWrites, tagsByStudent } from '../domain/tags';
import { useCollectionWindow } from './useCollectionWindow';

// Student tags, as a managed collection (#138).
//
// The shape is `useCategories` next door, minus its built-ins: there are **no
// built-in tags**, so nothing stands in for an empty collection and nothing is
// materialised on a first write. A studio with no tags has no tags, as in
// MMS's own empty state, so this reads and writes a plain collection.
//
// It keeps that file's `hasRead` guard: a write issued before the first
// snapshot is a write against a collection this app has not seen, and here it
// could land a save on a studio whose tags belong to the previously signed-in
// user.
//
// ── Membership is written here, not on the student ─────────────────────────
//
// `studentIds` is a field on the tag. So adding a student to "Piano" is one
// write to one document, and renaming "Piano" is also one write — there is no
// denormalised copy on the roster to carry, and no equivalent of
// `relabelCategory`. A category is one value on an event and must be readable
// without a second lookup, while a tag is a set and is only ever read as one.

const TagsContext = createContext(null);
// The same 200 the categories and locations lists use. Every query on `col()`
// must carry a limit or `firestore.rules` refuses the *list* outright, as a
// permission-denied that looks like an ordinary read failure (see `col` in
// paths.js).
const TRACK_LIMIT = 200;

export function TagsProvider({ children }) {
  const { user } = useAuth();

  // The tags window, from whichever read path serves this session (#207 U5).
  // No `withMetadata`: this listener never asked for metadata changes.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.tags,
    limit: TRACK_LIMIT,
  });
  const stored = read.docs;

  // Whether `stored` holds *this* user's tags — false before the first read,
  // and false again the moment the user changes. Not the same question as "has
  // loading finished" — see useCategories.jsx. The hook scopes the answer to
  // the signed-in uid on both paths.
  const hasRead = read.hasRead;
  const hasReadRef = useRef(false);
  hasReadRef.current = hasRead;

  const value = useMemo(() => {
    const tags = sortTags(stored);
    const byStudent = tagsByStudent(tags);

    return {
      loaded: hasRead,
      // Distinct from `loaded`, which is "is this *this* user's data". This is
      // "has the first read finished". Nothing here turns on it: a delete asks
      // the server (#318), and tag membership does not count as history
      // (#320).
      loading: read.loading,
      // The read stopped before it answered (#647) — src/domain/reading.js.
      failed: read.failed,
      tags,
      byId: (id) => tags.find((t) => t.id === id) || null,

      // One student's tags, already sorted. Empty array rather than null: every
      // caller renders a list, and a student with no tags is the common case.
      tagsFor: (studentId) => byStudent.get(studentId) || [],

      // Add or update one tag, per field, with membership as a delta.
      //
      // **A merge, and `studentIds` never crosses as an array.** `studentIds`
      // is a *set*, so a merge that merely omitted it would leave the old
      // membership in place and unticking every student would save as "no
      // change". A whole-document `set` loses for two devices (#198): A ticks
      // Ava offline, B ticks Ben offline, B's array is what it read plus Ben
      // without Ava, and the second `set` wins whole with no error.
      //
      // ── The delta, and why it is not plain `arrayUnion` ──────────────────
      //
      // The membership is written as the **difference** between what this
      // device last saw and what the drawer is saving: an `arrayUnion` of the
      // students it added and an `arrayRemove` of the ones it took out. Both
      // are computed by Firestore and commute there, so two devices editing
      // one tag offline both keep their edit and an untick still removes.
      //
      // Plain `arrayUnion` alone would make unticking a silent no-op (#198);
      // `eventSeries.exceptions` is grow-only and unions soundly, this field is
      // not. `arrayRemove` is carried through the write path (#288):
      // `core/src/reconcile.rs`'s `CLASS_MEMBER_SET` is the class, and the
      // constant in `core/src/queue.rs` says why a removal is replay-safe where
      // an `increment` is not.
      //
      // **Two writes to one document, in one batch.** Firestore cannot union
      // and remove on one field in one write, so a save that both adds and
      // removes is two merging `set`s on the same ref. `core/src/queue.rs`
      // permits that shape — a batch applies its writes in order rather than
      // refusing a repeated path — and the batch is atomic, so the two halves
      // cannot land apart. `writes.js` gives them distinct op ids from their
      // index, and `src/data/overlay.js` folds them in order, each keeping the
      // server's array rather than materialising one.
      //
      // ── The before-state comes from this provider, not from the drawer ────
      //
      // This provider holds the live snapshot the drawer rendered from, so the
      // delta is computed here against what this device last *saw*, which is
      // the baseline the two transforms are relative to.
      //
      // If `id` names a tag that is not in the window (`TRACK_LIMIT` is 200),
      // the before-state reads empty and the save becomes union-only: an
      // untick would not apply. The drawer can only edit a tag it listed, and
      // the failure keeps a membership rather than dropping one.
      //
      // A create writes no `studentIds` at all when nothing was ticked, rather
      // than an empty array. `tagMembers` in domain/tags.js reads an absent
      // field as no members, and `firestore.rules` has it as
      // `optionalTextList`. A materialised `[]` is the one write on this field
      // the core's `materialisedSet` rule refuses.
      //
      // MMS has no offline mode, so it has no two-device behaviour to mirror
      // here (#198, #288, #286); a single online drawer saves what MMS would.
      //
      // The name is not checked against the list here, as in `saveCategory`
      // (#129). Uniqueness lives in the drawer, driven by `tagErrors` in
      // domain/tags.js, because a duplicate name is for the person who typed
      // it, and a provider can only throw or silently drop.
      saveTag: async ({ id, label, kind, color, description, studentIds }) => {
        if (!user || !hasReadRef.current) return;
        const ref = docRef(user.uid, COLLECTIONS.tags, id || freeId('tag'));
        const { added, removed } = membershipDelta(
          id ? tags.find((t) => t.id === id) : null,
          studentIds,
        );
        // `tagWrites` in domain/tags.js. `tagKind` reads anything that is not
        // `group` as `instrument`, so a draft with no kind on it lands on
        // DEFAULT_TAG_KIND rather than writing a field the rules would refuse.
        const { fields, removal } = tagWrites(
          { label, kind, color, description },
          { added, removed },
          { serverTimestamp: serverTimestamp(), arrayUnion, arrayRemove },
        );
        const batch = writeBatch(db);
        batch.set(ref, fields, { merge: true });
        if (removal) batch.set(ref, removal, { merge: true });
        await batch.commit();
      },

      // Delete, with nothing else moved.
      //
      // As in MMS, this is not gated: its confirm names the tag and does not
      // mention, reassign or count the students on it. Membership lives on this
      // document, so there is nothing else to clean up: no roster write, no
      // cascade.
      removeTag: async (id) => {
        if (!user || !hasReadRef.current) return;
        const batch = writeBatch(db);
        batch.delete(docRef(user.uid, COLLECTIONS.tags, id));
        await batch.commit();
      },
    };
  }, [stored, hasRead, read.loading, read.failed, user]);

  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
}

export function useTags() {
  const ctx = useContext(TagsContext);
  if (!ctx) throw new Error('useTags must be used within a TagsProvider');
  return ctx;
}
